import { useRef, useState } from 'react';
import { Button, TableRowData } from 'tdesign-react';

import { ITableColumns } from '@/components';
import { FormTitle } from '@/components/form/materials/Title';
import DialogForm from '@/components/form/wrapper/DialogForm';
import { NovaImage } from '@/components/Image';
import { CoverName } from '@/components/list/coverName';
import { CoverVideo } from '@/components/list/coverVideo';
import SimpleSortTable from '@/components/table/simpleSortTable';
import { IBot } from '@/interface';
import { Bot, OthersData } from '@/interface/promotion';
import BotCard from '@/pages/index/bot';

import { LIST_FORM_CONFIG } from './form-config';
import styles from './index.module.less';

interface IProps {
  data: Bot['bots'];
  othersData: Bot['others'];
  moveBot: (id: string) => void;
  othersChange: (data: OthersData[]) => void;
  onChange: (data: TableRowData) => void;
}

const List: React.FC<IProps> = ({
  data = [],
  moveBot,
  onChange,
  othersChange,
  othersData = [],
}) => {
  const tableRef = useRef(null);
  const MAX_TABLE_WIDTH = 1400;
  const getOtherData = (id: string) => othersData.find(e => e?.id === id);
  const columns = [
    {
      colKey: 'avatar',
      title: '角色名称',
      width: 400,
      cell: ({ row }: { row: IBot }) => <BotCard item={row} showBtn={false} />,
    },
    {
      colKey: 'bgImage',
      title: '封面图',
      width: MAX_TABLE_WIDTH * 0.15,
      cell: ({ row }: { row: IBot }) => {
        const data = getOtherData(row?.id);
        return (
          <CoverName
            coverProps={{
              src: data?.bgImage,
            }}
          />
        );
      },
    },
    {
      colKey: 'bgVideo',
      title: '封面视频',
      width: MAX_TABLE_WIDTH * 0.15,
      cell: ({ row }: { row: IBot }) => {
        const data = getOtherData(row?.id);
        return (
          <CoverVideo
            coverProps={{
              src: data?.bgVideo,
            }}
          />
        );
      },
    },
    {
      colKey: 'leftLabel',
      title: '左上角图标',
      width: MAX_TABLE_WIDTH * 0.1,
      cell: ({ row }: { row: IBot }) => {
        const data = getOtherData(row?.id);
        return (
          <>
            <div className={styles.labelText}>{data?.leftLabel?.text}</div>
            <NovaImage
              style={{ maxWidth: 160, maxHeight: 40, overflow: 'scroll' }}
              src={data?.leftLabel?.icon}
            />
          </>
        );
      },
    },
    {
      colKey: 'rightLabel',
      title: '右上角图标',
      width: MAX_TABLE_WIDTH * 0.1,
      cell: ({ row }: { row: IBot }) => {
        const data = getOtherData(row?.id);
        return (
          <>
            <div className={styles.labelText}>{data?.rightLabel?.text}</div>
            <NovaImage
              style={{ maxWidth: 160, maxHeight: 40, overflow: 'scroll' }}
              src={data?.rightLabel?.icon}
            />
          </>
        );
      },
    },
    {
      colKey: 'op',
      title: '操作',
      width: 200,
      align: 'center' as 'center',
      cell: ({ row }: { row: IBot }) => (
        <div className={styles.options}>
          <Button
            onClick={() => handleEdit(row)}
            theme="default"
            variant="text"
          >
            新增配置
          </Button>
          <Button
            onClick={() => moveBot(row.id)}
            theme="default"
            variant="text"
          >
            移除
          </Button>
        </div>
      ),
    },
  ];

  const handleEdit = (row: IBot) => {
    const data = othersData.find(e => e.id === row?.id);
    setFormData({ ...data, id: row?.id });
    setVisible(true);
  };

  const getData = (data: Array<Record<string, any>>) => {
    onChange(data);
  };

  const [visible, setVisible] = useState(false);
  const [formData, setFormData] = useState({ id: '' });

  const onSubmit = async (values: OthersData) => {
    const hasValue = othersData?.some(e => e?.id === values?.id);
    const temp = othersData?.map((e) => {
      if (e.id === values.id) {
        return values;
      }
      return e;
    });
    othersChange(hasValue ? temp : [...temp, values]);
  };

  const handleClose = () => {
    setFormData({ id: '' });
    setVisible(false);
  };

  const onValuesChange = (content: Record<string, unknown>) => {
    setFormData({ ...formData, ...content });
  };

  return (
    <>
      <SimpleSortTable
        cRef={tableRef}
        columnsConfig={columns as unknown as ITableColumns[]}
        dataList={data}
        dragWidth={100}
        getData={getData}
        extendsConfig={{}}
      />
      <DialogForm
        fieldsConfig={LIST_FORM_CONFIG}
        onSubmit={onSubmit}
        onClose={handleClose}
        isVisible={Boolean(visible && formData?.id)}
        formData={{ ...formData }}
        formConfig={{
          layout: 'vertical',
          labelWidth: 120,
          labelAlign: 'top',
          onValuesChange,
        }}
        layOutConfig={{
          paddingBottom: 30,
          maxHeight: 700,
        }}
        dialogConfig={{
          header: <FormTitle title={'新增配置'} replaceObject={{}} />,
          style: { width: 800 },
          destroyOnClose: true,
        }}
        topRender={
          <div className={styles.tipText14}>
            该配置展示优先级高于角色默认配置，如设置了封面图，则封面图将覆盖角色默认封面图
          </div>
        }
      />
    </>
  );
};

export default List;
